import { useEffect, useState } from 'react';
import {
  fetchAndActivate,
  getBoolean,
  getString,
} from 'firebase/remote-config';
import { remoteConfig } from '@/lib/firebase';

type FeatureFlagType = 'boolean' | 'string' | 'json';

type FeatureFlagReturn<T extends FeatureFlagType> = T extends 'boolean'
  ? boolean
  : T extends 'json'
    ? Record<string, unknown> | null
    : string;

const getDefaultValue = (type: FeatureFlagType) => {
  if (type === 'boolean') return false;
  if (type === 'json') return null;
  return '';
};

const TRUTHY_STRINGS = ['true', '1', 'yes', 'on'];

/**
 * Local overrides take precedence over Remote Config. Read once at module
 * load from `REACT_APP_FEATURE_FLAG_OVERRIDES` (a JSON object mapping flag
 * key to value). Used by CI and local dev so gated routes exist without a
 * Firebase project. Invalid JSON logs one warning and is treated as `{}`.
 */
export const parseFeatureFlagOverrides = (
  raw: string | undefined
): Record<string, unknown> => {
  if (!raw || raw.trim() === '') return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
    console.warn(
      'REACT_APP_FEATURE_FLAG_OVERRIDES must be a JSON object; ignoring.'
    );
  } catch {
    console.warn(
      'REACT_APP_FEATURE_FLAG_OVERRIDES is not valid JSON; ignoring.'
    );
  }
  return {};
};

/**
 * Parsed once at module load. Exported (as a mutable map) so unit tests can
 * set/clear overrides without re-importing the module; app code must treat
 * it as read-only.
 */
export const featureFlagOverrides: Record<string, unknown> =
  parseFeatureFlagOverrides(process.env.REACT_APP_FEATURE_FLAG_OVERRIDES);

const coerceBoolean = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    return TRUTHY_STRINGS.includes(value.toLowerCase().trim());
  }
  return false;
};

const coerceOverride = <T extends FeatureFlagType>(
  value: unknown,
  type: T
): FeatureFlagReturn<T> => {
  if (type === 'boolean') {
    return coerceBoolean(value) as FeatureFlagReturn<T>;
  }
  if (type === 'json') {
    if (value && typeof value === 'object') {
      return value as FeatureFlagReturn<T>;
    }
    if (typeof value === 'string' && value) {
      try {
        return JSON.parse(value) as FeatureFlagReturn<T>;
      } catch {
        return null as FeatureFlagReturn<T>;
      }
    }
    return null as FeatureFlagReturn<T>;
  }
  if (typeof value === 'string') return value as FeatureFlagReturn<T>;
  if (value === null || value === undefined) {
    return '' as FeatureFlagReturn<T>;
  }
  return (
    typeof value === 'object' ? JSON.stringify(value) : String(value)
  ) as FeatureFlagReturn<T>;
};

const useFeatureFlag = <T extends FeatureFlagType>(
  flagKey: string,
  type: T = 'boolean' as T
): FeatureFlagReturn<T> => {
  const hasOverride = Object.prototype.hasOwnProperty.call(
    featureFlagOverrides,
    flagKey
  );

  const [flagValue, setFlagValue] = useState<FeatureFlagReturn<T>>(() =>
    hasOverride
      ? coerceOverride(featureFlagOverrides[flagKey], type)
      : (getDefaultValue(type) as FeatureFlagReturn<T>)
  );

  useEffect(() => {
    if (hasOverride) {
      setFlagValue(coerceOverride(featureFlagOverrides[flagKey], type));
      return;
    }

    if (!remoteConfig) {
      console.warn('Remote Config is not available during SSR.');
      return;
    }

    const fetchFeatureFlag = async () => {
      try {
        await fetchAndActivate(remoteConfig);

        let value: FeatureFlagReturn<T> = getDefaultValue(
          type
        ) as FeatureFlagReturn<T>;

        if (type === 'boolean') {
          const boolValue = getBoolean(remoteConfig, flagKey);
          if (!boolValue) {
            const rawValue = getString(remoteConfig, flagKey)
              .toLowerCase()
              .trim();
            value = (
              TRUTHY_STRINGS.includes(rawValue) ? true : boolValue
            ) as FeatureFlagReturn<T>;
          } else {
            value = boolValue as FeatureFlagReturn<T>;
          }
        } else if (type === 'json') {
          const jsonString = getString(remoteConfig, flagKey);
          value = (
            jsonString ? JSON.parse(jsonString) : null
          ) as FeatureFlagReturn<T>;
        } else {
          value = getString(remoteConfig, flagKey) as FeatureFlagReturn<T>;
        }

        setFlagValue(value);
      } catch (error) {
        console.error('Error fetching or parsing feature flag:', error);
        setFlagValue(getDefaultValue(type) as FeatureFlagReturn<T>);
      }
    };

    fetchFeatureFlag();
  }, [flagKey, type, hasOverride]);

  return flagValue;
};

export default useFeatureFlag;
